import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import flagsmith from 'flagsmith'
import Cookies from 'js-cookie'
import { BotOff, OctagonAlert } from 'lucide-react'
import ReCAPTCHA from 'react-google-recaptcha'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { COOKIES_KEYS } from '@/common/constants/cookiesKeys'
import { ENV } from '@/common/constants/env'
import { ROUTES } from '@/common/constants/routes'
import { getUserInfo } from '@/common/hooks/useGetUserInfo'
import { useLanguageSync } from '@/common/hooks/useLanguageSync'
import { parseJwt } from '@/common/utils/parseJwt'
import { api } from '@/lib/axios'
import i18n from '@/lib/i18n'
import { SignInForm } from '../schemas/SignInSchema'

export type SignInResponse = {
  id_token: string
  scope: string
  access_token: string
  expires_in: number
  refresh_token: string
  refresh_expires_in: number
  session_state: string
}

type PostOauthTokenParams = {
  username: string
  password: string
}

async function postOauthToken({ username, password }: PostOauthTokenParams) {
  return api.backend.post<SignInResponse>('/oauth/token', {
    username,
    password,
  })
}

export function useSignIn() {
  const [loading, setLoading] = useState(false)

  const navigate = useNavigate()
  const reCaptchaRef = useRef<ReCAPTCHA | null>(null)
  const { t } = useTranslation('login')
  const { handleLanguageSync } = useLanguageSync()

  const { mutateAsync, isPending } = useMutation({
    mutationKey: ['signIn'],
    mutationFn: postOauthToken,
  })

  useLayoutEffect(() => {
    window?.HSStaticMethods?.autoInit()
  }, [])

  const identifyUser = async (accessToken: string, rememberMe?: boolean) => {
    try {
      const { exp } = parseJwt<{ userType: string; exp: number }>(accessToken)

      Cookies.set(COOKIES_KEYS.token, accessToken, {
        expires: rememberMe ? exp : undefined,
        sameSite: 'strict',
      })

      const userInfo = await getUserInfo()
      const { Person, UserPreference, ...user } = userInfo

      await flagsmith.identify(user.email)
      flagsmith.startListening(10 * 60 * 1000) //  10 minutes

      if (UserPreference?.locale) {
        const currentLanguage = i18n.language
        const preferenceLocale = UserPreference.locale

        handleLanguageSync(currentLanguage, preferenceLocale, () => {
          navigate(ROUTES.PANEL.NEWS, { replace: true })
        })
      } else {
        navigate(ROUTES.PANEL.NEWS, { replace: true })
      }
    } catch {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: OctagonAlert,
          title: t('sign_in.toast.error.title'),
          description: t('sign_in.toast.error.message'),
        },
      })
    }
  }

  const handleSignIn = async (data: SignInForm) => {
    setLoading(true)

    if (ENV.VITE_RECAPTCHA_ENABLED) {
      try {
        if (!reCaptchaRef?.current) {
          return toast(Toast, {
            data: {
              type: 'danger',
              icon: BotOff,
              title: t('sign_in.toast.re_captcha.title'),
              description: t('sign_in.toast.re_captcha.message'),
            },
          })
        }

        reCaptchaRef.current.reset()
        const token = await reCaptchaRef.current.executeAsync()

        if (!token) {
          return toast(Toast, {
            data: {
              type: 'danger',
              icon: BotOff,
              title: t('sign_in.toast.re_captcha.title'),
              description: t('sign_in.toast.re_captcha.message'),
            },
          })
        }
      } catch {
        return toast(Toast, {
          data: {
            type: 'danger',
            icon: BotOff,
            title: t('sign_in.toast.re_captcha.title'),
            description: t('sign_in.toast.re_captcha.message'),
          },
        })
      }
    }

    try {
      const response = await mutateAsync({
        username: data.username,
        password: data.password,
      })

      await identifyUser(response.data.access_token, data.rememberMe)
    } catch {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: OctagonAlert,
          title: t('sign_in.toast.error.title'),
          description: t('sign_in.toast.error.message'),
        },
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    return () => setLoading(false)
  }, [])

  return {
    identifyUser,
    handleSignIn,
    reCaptchaRef,
    isPendingSignIn: isPending || loading,
  }
}
